import { AlertCircle, ArrowUpRight, X } from 'lucide-react';
import { Link } from 'react-router';
import { useDeviceDetails, type DeviceInterface } from '../hooks/useDeviceDetails';
import type { StpOverlayDeviceState } from '../hooks/useStpOverlay';
import type { LinkSpeed } from '../types/layers';
import {
  getVlanColor,
  getLinkSpeedColor,
  getLinkSpeedLabel,
} from '../utils/layerRenderers';
import ResourceTags from './ResourceTags';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import './DeviceDetailPanel.css';
import { DeviceTrafficSection } from './traffic/DeviceTrafficSection';
import type { TrafficRange } from './traffic/range';

/** Information about a connected remote device/interface */
export interface ConnectedEndpoint {
  deviceId: string;
  deviceName: string | null;
  deviceIp: string;
  interfaceName?: string;
}

/** Map of local interface ID to the remote endpoint it connects to */
export type InterfaceConnectionMap = Map<string, ConnectedEndpoint>;

/**
 * Expands highlighted interface IDs to include LAG member interfaces.
 * If a highlighted interface is a LAG, its members are also highlighted.
 * Checks both childRelationships and parentRelationships since the data model
 * may represent LAG membership in either direction.
 */
function expandHighlightedIds(highlightedIds: string[], interfaces: DeviceInterface[]): string[] {
  if (highlightedIds.length === 0) return highlightedIds;

  const expanded = new Set(highlightedIds);

  // Build a map of interface ID to interface for quick lookup
  const interfaceMap = new Map(interfaces.map((iface) => [iface.id, iface]));

  // For each highlighted interface, check if it's a LAG and add its members
  highlightedIds.forEach((id) => {
    const iface = interfaceMap.get(id);
    if (!iface) return;

    // Check childRelationships (standard: LAG has members as children)
    if (iface.childRelationships) {
      iface.childRelationships.forEach((child) => {
        expanded.add(child.interfaceId);
      });
    }

    // Also check parentRelationships (inverted: LAG lists members as parents)
    // This handles cases where the data model has reversed relationship direction
    if (iface.parentRelationships) {
      iface.parentRelationships.forEach((parent) => {
        expanded.add(parent.interfaceId);
      });
    }
  });

  return Array.from(expanded);
}

/** A connected link as seen by the physical layer */
export interface PhysicalLinkContext {
  remote: string;
  speed: LinkSpeed;
}

/** A connected link as seen by the throughput layer */
export interface ThroughputLinkContext {
  remote: string;
  /** Higher of the two directions (0-100), or `null` when the link speed is unknown. */
  utilization: number | null;
  /** Combined current bps across both directions */
  totalBps: number;
  hasErrors: boolean;
}

/** A VLAN this device participates in */
export interface VlanContext {
  vlanId: number;
  name: string;
  isSelected: boolean;
}

/**
 * Layer-specific context for the selected device, computed by the parent
 * (Views) where the per-layer overlay data already lives. The detail panel
 * renders a matching section so the panel reflects whatever layer the
 * operator is currently looking at. `topology`/`ospf` carry no extra section.
 */
export type DeviceLayerContext =
  | { layer: 'topology' | 'ospf' }
  | { layer: 'physical'; links: PhysicalLinkContext[] }
  | { layer: 'vlan'; vlans: VlanContext[] }
  | { layer: 'stp'; instanceLabel: string; state: StpOverlayDeviceState | null }
  | { layer: 'throughput'; links: ThroughputLinkContext[] };

interface DeviceDetailPanelProps {
  deviceId: string | null;
  onClose?: () => void;
  /** Interface ID to highlight (from URL - will auto-expand to include LAG members) */
  highlightedInterfaceIds?: string[];
  /** Callback when an interface is clicked - receives device ID and interface ID */
  onInterfaceClick?: (deviceId: string, interfaceId: string) => void;
  /** Map of interface ID to connected remote device info (for tooltip display) */
  interfaceConnections?: InterfaceConnectionMap;
  /** Active visualization layer context, drives the layer-specific section */
  layerContext?: DeviceLayerContext;
  /** Range inherited from the page, so the panel and the canvas agree. */
  trafficRange?: TrafficRange;
}

/**
 * DeviceDetailPanel - Displays detailed information about a network device
 * including a visual port layout similar to physical network switches/routers
 */
export default function DeviceDetailPanel({ deviceId, onClose, highlightedInterfaceIds = [], onInterfaceClick, interfaceConnections, layerContext, trafficRange }: DeviceDetailPanelProps) {
  const { data: device, isLoading, error } = useDeviceDetails({
    deviceId,
    includeInterfaces: true,
  });

  // The traffic surfaces and the panel both speak interface id (gateway#102).
  const handleTrafficPortSelect = onInterfaceClick
    ? (interfaceId: string) => {
        if (deviceId) onInterfaceClick(deviceId, interfaceId);
      }
    : undefined;

  if (!deviceId) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Select a device to view details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Loading device details…</p>
        <Progress value={undefined} className="h-1 mt-2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading device:</strong>{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Device not found</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-card p-4 device-detail-panel">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold truncate">{device.name || device.ipAddress}</h3>
          <Link
            to={`/devices/${deviceId}`}
            className="inline-flex items-center gap-1 text-xs text-sev-5 hover:underline"
          >
            View full details
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        {onClose ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>

      <div className="mb-4">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border-subtle">
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">
                Status
              </th>
              <td className="py-1.5">
                <Badge
                  className={cn(
                    'border-0 font-normal capitalize',
                    getStatusBadgeClass(device.status),
                  )}
                >
                  {device.status}
                </Badge>
              </td>
            </tr>
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                IP address
              </th>
              <td className="py-1.5">
                <code className="font-mono text-xs">{device.ipAddress || 'N/A'}</code>
              </td>
            </tr>
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Type
              </th>
              <td className="py-1.5">{device.deviceType || 'Unknown'}</td>
            </tr>
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Polled by
              </th>
              <td className="py-1.5">
                {device.assignedGatewayId ? (
                  <span className="inline-flex items-center gap-1.5">
                    {device.assignedGatewayName ?? `Gateway ${device.assignedGatewayId.slice(0, 8)}`}
                    {device.assignedGatewayStatus ? (
                      <Badge
                        className={cn(
                          'border-0 font-normal capitalize',
                          getStatusBadgeClass(device.assignedGatewayStatus),
                        )}
                      >
                        {device.assignedGatewayStatus}
                      </Badge>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Region
              </th>
              <td className="py-1.5">{device.regionName}</td>
            </tr>
            {device.divisionName ? (
              <tr>
                <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                  Division
                </th>
                <td className="py-1.5">{device.divisionName}</td>
              </tr>
            ) : null}
            {device.description ? (
              <tr>
                <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                  Description
                </th>
                <td className="py-1.5">{device.description}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {layerContext ? <LayerContextSection context={layerContext} /> : null}

      {/* Above the port map, deliberately: "which port" has to be answerable
          without scrolling past a 125-port visualization to find it. */}
      <DeviceTrafficSection deviceId={deviceId} range={trafficRange} onSelectInterface={handleTrafficPortSelect} />

      <ResourceTags resourceType="device" resourceId={deviceId} />

      {device.interfaces && device.interfaces.length > 0 ? (
        <div className="port-visualization mt-4">
          <h4 className="text-sm font-semibold mb-3">
            Interfaces ({device.interfaces.length})
          </h4>
          <PortLayout
            interfaces={device.interfaces}
            highlightedInterfaceIds={expandHighlightedIds(
              highlightedInterfaceIds,
              device.interfaces,
            )}
            onInterfaceClick={
              onInterfaceClick
                ? (interfaceId) => { onInterfaceClick(deviceId, interfaceId); }
                : undefined
            }
            interfaceConnections={interfaceConnections}
          />
        </div>
      ) : null}

      {device.interfaces && device.interfaces.length === 0 ? (
        <Alert className="mt-4 border-sev-5/30 bg-sev-5/5">
          <AlertDescription>No interfaces discovered for this device yet.</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

/**
 * LayerContextSection - Renders a compact, layer-specific summary for the
 * selected device that mirrors whatever overlay the operator is viewing on
 * the graph (VLAN membership, STP role, link speeds, throughput). Returns
 * null for layers with no per-device data (topology, ospf).
 */
function LayerContextSection({ context }: { context: DeviceLayerContext }) {
  switch (context.layer) {
    case 'vlan':
      return (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">VLAN membership</h4>
          {context.vlans.length === 0 ? (
            <p className="text-xs text-muted-foreground">No VLANs configured on this device.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {context.vlans.map((vlan) => {
                const color = getVlanColor(vlan.vlanId);
                return (
                  <span
                    key={vlan.vlanId}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs',
                      vlan.isSelected ? 'font-semibold' : 'font-normal',
                    )}
                    style={{
                      borderColor: color,
                      backgroundColor: color + (vlan.isSelected ? '28' : '12'),
                    }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                    {vlan.vlanId}: {vlan.name}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      );

    case 'stp':
      return (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">
            Spanning tree{context.instanceLabel ? ` · ${context.instanceLabel}` : ''}
          </h4>
          {!context.state ? (
            <p className="text-xs text-muted-foreground">
              No STP data reported for this instance on this device.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border-subtle">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">
                    Role
                  </th>
                  <td className="py-1.5">
                    {context.state.isRootBridge ? (
                      <Badge className="border-0 font-normal bg-sev-4/15 text-sev-4">
                        Root bridge
                      </Badge>
                    ) : (
                      'Non-root'
                    )}
                  </td>
                </tr>
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                    Bridge ID
                  </th>
                  <td className="py-1.5">
                    <code className="font-mono text-xs">{context.state.bridgeId}</code>
                  </td>
                </tr>
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                    Root ID
                  </th>
                  <td className="py-1.5">
                    <code className="font-mono text-xs">{context.state.rootId}</code>
                  </td>
                </tr>
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                    Topology changes
                  </th>
                  <td className="py-1.5">
                    {context.state.topologyChanges ?? (
                      <span className="text-muted-foreground">not reported by this device</span>
                    )}
                  </td>
                </tr>
                {context.state.lastTopologyChange ? (
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                      Last change
                    </th>
                    <td className="py-1.5">{formatTimestamp(context.state.lastTopologyChange)}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </div>
      );

    // The 'throughput' case is gone. It drew a per-link utilization bar coloured
    // by `getUtilizationColor`, fed by a capacity that was invented when the
    // real one was missing — a saturated 100M port read 10% and most of the
    // fleet painted a confident healthy green. Capacity is nullable now, so
    // there is nothing honest to feed it, and the traffic section above answers
    // the same question from measured numbers.

    case 'physical':
      return (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">Link speeds</h4>
          {context.links.length === 0 ? (
            <p className="text-xs text-muted-foreground">No connected links for this device.</p>
          ) : (
            <div className="space-y-1">
              {context.links.map((link, index) => (
                <div
                  key={`${link.remote}-${String(index)}`}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="truncate mr-2">{link.remote}</span>
                  <span className="inline-flex items-center gap-1.5 shrink-0">
                    <span
                      className="h-1 w-5 rounded"
                      style={{ backgroundColor: getLinkSpeedColor(link.speed) }}
                    />
                    {getLinkSpeedLabel(link.speed)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      );

    default:
      // topology / ospf — no per-device overlay data to surface
      return null;
  }
}

/**
 * PortLayout - Renders ports in a switch-like visual layout
 * - Separates physical ports from LAGs/virtual interfaces
 * - Shows LAG membership relationships
 * - Groups of 4 ports per row
 * - Max 2 rows per section for display
 */
function PortLayout({ interfaces, highlightedInterfaceIds, onInterfaceClick, interfaceConnections }: { interfaces: DeviceInterface[]; highlightedInterfaceIds: string[]; onInterfaceClick?: (interfaceId: string) => void; interfaceConnections?: InterfaceConnectionMap }) {
  // Categorize interfaces by type
  const physicalPorts: DeviceInterface[] = [];
  const lagInterfaces: DeviceInterface[] = [];
  const virtualInterfaces: DeviceInterface[] = [];

  interfaces.forEach((iface) => {
    const type = iface.interfaceType?.toLowerCase() || '';
    const name = iface.interfaceName.toLowerCase();
    const operStatus = iface.operStatus?.toLowerCase() || '';

    // Skip interfaces that are just numbers (likely invalid/placeholder data)
    if (/^\d+$/.test(iface.interfaceName)) {
      return;
    }

    // Skip "other" and "unknown" interface types (not useful for display)
    if (type === 'other' || type === 'unknown') {
      return;
    }

    // Skip tunnel/LAG/virtual interfaces that are notPresent
    const isVirtualType = type === 'tunnel' || type === 'ieee8023adlag' || type === 'virtual' ||
                          type === 'l2vlan' || type === 'l3ipvlan' ||
                          name.includes('tunnel') || name.includes('loopback');
    if (isVirtualType && operStatus === 'notpresent') {
      return;
    }

    // LAG interfaces - check both type and name
    if (type === 'ieee8023adlag' || name.includes('port-channel') || name.includes('lag')) {
      lagInterfaces.push(iface);
    }
    // Virtual interfaces (VLANs, loopbacks, tunnels)
    else if (type === 'virtual' || type === 'l2vlan' || type === 'l3ipvlan' || type === 'tunnel' ||
             name.includes('vlan') || name.includes('loopback')) {
      virtualInterfaces.push(iface);
    }
    // Physical ethernet ports
    else {
      physicalPorts.push(iface);
    }
  });

  // Sort each group by ifindex
  const sortByIfindex = (a: DeviceInterface, b: DeviceInterface) => {
    const aIndex = a.ifindex ? parseInt(a.ifindex, 10) : null;
    const bIndex = b.ifindex ? parseInt(b.ifindex, 10) : null;

    if (aIndex !== null && bIndex !== null) {
      return aIndex - bIndex;
    }

    // Interfaces with ifindex sort before those without
    if (aIndex !== null) return -1;
    if (bIndex !== null) return 1;

    // Fall back to natural name sorting
    return a.interfaceName.localeCompare(b.interfaceName, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  const sortedPhysical = physicalPorts.sort(sortByIfindex);
  const sortedLags = lagInterfaces.sort(sortByIfindex);
  const sortedVirtual = virtualInterfaces.sort(sortByIfindex);

  return (
    <div className="port-layout">
      {sortedLags.length > 0 ? (
        <div className="mb-5">
          <h5 className="text-sm font-semibold mb-3">LAG interfaces ({sortedLags.length})</h5>
          <InterfaceSection
            interfaces={sortedLags}
            highlightedInterfaceIds={highlightedInterfaceIds}
            onInterfaceClick={onInterfaceClick}
            interfaceConnections={interfaceConnections}
          />
        </div>
      ) : null}

      {sortedPhysical.length > 0 ? (
        <div className="mb-5">
          <h5 className="text-sm font-semibold mb-3">
            Physical ports ({sortedPhysical.length})
          </h5>
          <InterfaceSection
            interfaces={sortedPhysical}
            highlightedInterfaceIds={highlightedInterfaceIds}
            onInterfaceClick={onInterfaceClick}
            interfaceConnections={interfaceConnections}
          />
        </div>
      ) : null}

      {sortedVirtual.length > 0 ? (
        <div className="mb-5">
          <h5 className="text-sm font-semibold mb-3">
            Virtual interfaces ({sortedVirtual.length})
          </h5>
          <InterfaceSection
            interfaces={sortedVirtual}
            highlightedInterfaceIds={highlightedInterfaceIds}
            onInterfaceClick={onInterfaceClick}
            interfaceConnections={interfaceConnections}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * InterfaceSection - Renders a section of interfaces in rows
 * Displays ports in physical network device layout:
 * - Odd-numbered ports on top row (1, 3, 5, 7...)
 * - Even-numbered ports on bottom row (2, 4, 6, 8...)
 * - Grouped in sets of 8 ports (4 odd + 4 even) with visual separation
 * - LAG members highlighted with colored boxes
 */
function InterfaceSection({ interfaces, highlightedInterfaceIds, onInterfaceClick, interfaceConnections }: { interfaces: DeviceInterface[]; highlightedInterfaceIds: string[]; onInterfaceClick?: (interfaceId: string) => void; interfaceConnections?: InterfaceConnectionMap }) {
  // Create a Set for O(1) lookup of highlighted interfaces
  const highlightedSet = new Set(highlightedInterfaceIds);

  // Sort by ifindex and assign display positions
  const portsWithNumbers = interfaces
    .map((iface) => ({
      interface: iface,
      ifindex: iface.ifindex ? parseInt(iface.ifindex, 10) : 999999,
    }))
    .sort((a, b) => a.ifindex - b.ifindex)
    .map((item, index) => ({
      interface: item.interface,
      portNumber: index + 1, // Assign sequential port numbers based on ifindex order
    }));

  // Limit display to 48 ports max
  const maxPorts = 48;
  const displayPorts = portsWithNumbers.slice(0, maxPorts);

  // Split into odd and even based on PORT NUMBER (not array index)
  const oddPorts: (DeviceInterface & { portNumber: number })[] = [];
  const evenPorts: (DeviceInterface & { portNumber: number })[] = [];

  displayPorts.forEach(({ interface: iface, portNumber }) => {
    if (portNumber % 2 === 1) {
      oddPorts.push({ ...iface, portNumber }); // Port 1, 3, 5, 7, 9...
    } else {
      evenPorts.push({ ...iface, portNumber }); // Port 2, 4, 6, 8, 10...
    }
  });

  // Group ports into sets of 4 for visual separation (groups of 8 total: 4 odd + 4 even)
  const oddGroups = chunkArray(oddPorts, 4);
  const evenGroups = chunkArray(evenPorts, 4);

  // Build LAG group mapping for coloring
  const lagGroups = buildLagGroups(interfaces);

  // Pair odd and even groups together for proper vertical alignment
  const maxGroups = Math.max(oddGroups.length, evenGroups.length);
  const pairedGroups: Array<{
    odd: typeof oddPorts | undefined;
    even: typeof evenPorts | undefined;
  }> = [];

  for (let i = 0; i < maxGroups; i++) {
    pairedGroups.push({
      odd: oddGroups[i],
      even: evenGroups[i],
    });
  }

  return (
    <div>
      <div className="port-panel">
        {/* Render paired groups - each group has odd ports on top, even on bottom */}
        {pairedGroups.map((pair, pairIndex) => (
          <div key={`pair-${String(pairIndex)}`} className="port-pair">
            {/* Top row - odd numbered ports */}
            {pair.odd && (
              <div className="port-row mb-2">
                <div className="port-group">
                  {pair.odd.map((iface) => (
                    <PortIndicator
                      key={iface.id}
                      interface={iface}
                      lagGroup={lagGroups.get(iface.id)}
                      isHighlighted={highlightedSet.has(iface.id)}
                      onClick={onInterfaceClick ? () => { onInterfaceClick(iface.id); } : undefined}
                      connectedTo={interfaceConnections?.get(iface.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bottom row - even numbered ports */}
            {pair.even && (
              <div className="port-row">
                <div className="port-group">
                  {pair.even.map((iface) => (
                    <PortIndicator
                      key={iface.id}
                      interface={iface}
                      lagGroup={lagGroups.get(iface.id)}
                      isHighlighted={highlightedSet.has(iface.id)}
                      onClick={onInterfaceClick ? () => { onInterfaceClick(iface.id); } : undefined}
                      connectedTo={interfaceConnections?.get(iface.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {interfaces.length > displayPorts.length ? (
        <p className="text-xs text-muted-foreground mt-2">
          Showing {displayPorts.length} of {interfaces.length} interfaces
        </p>
      ) : null}
    </div>
  );
}

/**
 * Build LAG group mapping for coloring
 * Returns Map<interfaceId, lagGroupInfo>
 */
function buildLagGroups(interfaces: DeviceInterface[]): Map<string, { lagName: string; color: string }> {
  const lagMap = new Map<string, { lagName: string; color: string }>();
  const lagColors = ['#3273dc', '#48c774', '#ffdd57', '#f14668', '#00d1b2', '#ff3860', '#209cee', '#363636'];
  let colorIndex = 0;

  // Find all LAG interfaces
  const lagInterfaces = interfaces.filter((iface) => {
    const type = iface.interfaceType?.toLowerCase() || '';
    const name = iface.interfaceName.toLowerCase();
    return type === 'ieee8023adlag' || name.includes('port-channel') || name.includes('lag');
  });

  // For each LAG, assign color to all its members
  lagInterfaces.forEach((lagIface) => {
    const color = lagColors[colorIndex % lagColors.length];
    colorIndex++;

    // Add all child interfaces (members) to the map
    if (lagIface.childRelationships) {
      lagIface.childRelationships.forEach((child) => {
        lagMap.set(child.interfaceId, {
          lagName: lagIface.interfaceName,
          color: color,
        });
      });
    }
  });

  return lagMap;
}

/**
 * PortIndicator - Individual port visual indicator with hover details
 */
function PortIndicator({
  interface: iface,
  lagGroup,
  isHighlighted,
  onClick,
  connectedTo,
}: {
  interface: DeviceInterface;
  lagGroup?: { lagName: string; color: string };
  isHighlighted?: boolean;
  onClick?: () => void;
  connectedTo?: ConnectedEndpoint;
}) {
  const isUp = iface.operStatus?.toLowerCase() === 'up';
  const hasMembership = (iface.parentRelationships?.length || 0) > 0;
  const hasChildren = (iface.childRelationships?.length || 0) > 0;
  const isClickable = Boolean(onClick);

  let portClass = isUp ? 'port-up' : 'port-down';
  if (hasMembership) portClass += ' has-parent';
  if (hasChildren) portClass += ' has-children';
  if (isHighlighted) portClass += ' is-highlighted';
  if (isClickable) portClass += ' is-clickable';

  // Apply LAG group styling or highlight styling
  const portStyle: React.CSSProperties = isHighlighted
    ? {
        borderColor: '#ff3860',
        borderWidth: '3px',
        borderStyle: 'solid',
        boxShadow: '0 0 10px #ff386080',
        animation: 'pulse-highlight 1s ease-in-out 3',
        transform: 'scale(1.05)',
      }
    : lagGroup
      ? {
          borderColor: lagGroup.color,
          borderWidth: '3px',
          borderStyle: 'solid',
          boxShadow: `0 0 5px ${lagGroup.color}40`,
        }
      : {};

  return (
    <div
      className={`port-indicator ${portClass}`}
      style={portStyle}
      title={getPortTooltip(iface)}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); } : undefined}
    >
      <div className="port-led"></div>
      <div className="port-name">{formatPortName(iface.interfaceName)}</div>
      <div className="port-status-text">{formatPortStatus(iface)}</div>

      {/* LAG membership indicator */}
      {hasMembership && lagGroup && (
        <div className="port-badge" title={`Member of ${lagGroup.lagName}`}>
          🔗
        </div>
      )}

      {/* Hover detail tooltip */}
      <div className="port-tooltip">
        <strong>{iface.interfaceName}</strong>
        <div className="mt-1">
          <div><strong>Type:</strong> {iface.interfaceType || 'Unknown'}</div>
          <div><strong>Status:</strong> {iface.operStatus || 'Unknown'}</div>
          {iface.speedMbps && (
            <div><strong>Speed:</strong> {formatSpeed(iface.speedMbps)}</div>
          )}
          {iface.description && (
            <div><strong>Description:</strong> {iface.description}</div>
          )}
          {iface.macAddress && (
            <div><strong>MAC:</strong> {iface.macAddress}</div>
          )}

          {/* Connected device info */}
          {connectedTo && (
            <div className="mt-2 border-t border-border pt-2">
              <strong>Connected to:</strong>
              <div className="ml-2">
                {connectedTo.deviceName || connectedTo.deviceIp}
                {connectedTo.interfaceName && (
                  <span> ({connectedTo.interfaceName})</span>
                )}
              </div>
            </div>
          )}

          {/* Parent relationships (LAG membership) */}
          {iface.parentRelationships && iface.parentRelationships.length > 0 && (
            <div className="mt-2">
              <strong>Member of:</strong>
              {iface.parentRelationships.map((rel) => (
                <div key={rel.interfaceId} className="ml-2">
                  • {rel.interfaceName} ({rel.relationshipType})
                </div>
              ))}
            </div>
          )}

          {/* Child relationships (LAG members) */}
          {iface.childRelationships && iface.childRelationships.length > 0 && (
            <div className="mt-2">
              <strong>Members ({iface.childRelationships.length}):</strong>
              {iface.childRelationships.map((rel) => (
                <div key={rel.interfaceId} className="ml-2">
                  • {rel.interfaceName}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper functions

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

function getStatusBadgeClass(status: string): string {
  const statusLower = status.toLowerCase();
  if (statusLower === 'online') return 'bg-sev-4/15 text-sev-4';
  if (statusLower === 'offline') return 'bg-sev-1/15 text-sev-1';
  if (statusLower === 'connecting') return 'bg-sev-3/15 text-foreground';
  if (statusLower === 'failed') return 'bg-sev-1/15 text-sev-1';
  // Neutral, and deliberately so for `unknown`: it is a real answer — nobody is
  // currently in a position to say whether the device is alive — and the device
  // may be perfectly healthy, so it must not read as a fault.
  return 'bg-muted text-muted-foreground';
}

function formatPortName(name: string): string {
  // Shorten common interface names for display
  // e.g., "GigabitEthernet0/0/1" -> "Gi0/0/1"
  return name
    .replace('GigabitEthernet', 'Gi')
    .replace('FastEthernet', 'Fa')
    .replace('TenGigabitEthernet', 'Te')
    .replace('ethernet', 'eth');
}

function formatPortStatus(iface: DeviceInterface): string {
  const status = iface.operStatus?.toLowerCase();
  if (status === 'up' && iface.speedMbps) {
    return formatSpeed(iface.speedMbps);
  }
  return status === 'up' ? 'UP' : 'DOWN';
}

function formatSpeed(speedMbps: number): string {
  if (speedMbps >= 1000) {
    return `${String(speedMbps / 1000)}G`;
  }
  return `${String(speedMbps)}M`;
}

function getPortTooltip(iface: DeviceInterface): string {
  const parts = [
    iface.interfaceName,
    `Status: ${iface.operStatus || 'Unknown'}`,
  ];
  if (iface.speedMbps) {
    parts.push(`Speed: ${formatSpeed(iface.speedMbps)}`);
  }
  if (iface.description) {
    parts.push(`Desc: ${iface.description}`);
  }
  return parts.join('\n');
}

function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
